/* Native section controller: one device source, poster-first, no commerce hooks. */
const roots = new Map();
class RopetSection {
  constructor(root) {
    this.root = root;
    this.abort = new AbortController();
    this.signal = this.abort.signal;
    this.mobile = matchMedia('(max-width: 800px)');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.scenes = [...root.querySelectorAll('[data-rp-v2-scene]')];
    this.triggers = [...root.querySelectorAll('[data-rp-v2-trigger]')];
    this.videos = [...root.querySelectorAll('video')];
    this.bars = [...root.querySelectorAll('.rp-v2-progress i')];
    this.active = 0;
    this.visible = false;
    this.near = root.dataset.rpV2Kind === 'hero';
    this.frame = 0;
    this.destroyed = false;
    this.pinned = null;
    for (const video of this.videos) {
      video.muted = true;
      video.addEventListener('playing', () => video.parentElement.classList.add('rp-v2-loaded'), {signal:this.signal});
      video.addEventListener('error', () => video.parentElement.classList.remove('rp-v2-loaded'), {signal:this.signal});
    }
    this.nearObserver = new IntersectionObserver(entries => {
      this.near = entries[0].isIntersecting;
      this.sync();
    }, {rootMargin:'200px 0px'});
    this.nearObserver.observe(root);
    this.visibleObserver = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.sync();
    });
    this.visibleObserver.observe(root);
    this.mobile.addEventListener('change', () => {this.release(); this.sync();}, {signal:this.signal});
    this.reduced.addEventListener('change', () => {this.release(); this.sync();}, {signal:this.signal});
    document.addEventListener('visibilitychange', () => this.sync(), {signal:this.signal});
    if (this.scenes.length) {
      const queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame=0;this.update();});
      };
      window.addEventListener('scroll', queue, {passive:true,signal:this.signal});
      window.addEventListener('resize', queue, {passive:true,signal:this.signal});
      root.addEventListener('shopify:block:select', event => {
        const index = this.scenes.findIndex(scene => scene.dataset.rpV2Scene === event.detail.blockId);
        if (index >= 0) {this.pinned=index;this.activate(index);}
      }, {signal:this.signal});
      root.addEventListener('shopify:block:deselect', () => {this.pinned=null;this.update();}, {signal:this.signal});
    }
    this.update();
  }
  update() {
    if (this.destroyed) return;
    if (this.triggers.length) {
      const center = innerHeight / 2;
      let index = 0;
      this.triggers.forEach((trigger,i) => {if(trigger.getBoundingClientRect().top <= center) index=i;});
      this.activate(this.pinned ?? index);
    } else this.sync();
  }
  activate(index) {
    this.active = index;
    this.scenes.forEach((scene,i) => {
      scene.classList.toggle('rp-v2-active', i===index);
      scene.setAttribute('aria-hidden', String(i!==index));
    });
    this.bars.forEach((bar,i) => bar.classList.toggle('rp-v2-active', i===index));
    this.sync();
  }
  release() {
    this.videos.forEach(video => {
      video.pause();video.removeAttribute('src');video.removeAttribute('poster');video.load();
      video.parentElement.classList.remove('rp-v2-loaded');
    });
  }
  sync() {
    if (this.destroyed) return;
    this.videos.forEach((video,i) => {
      const active = !this.scenes.length || i===this.active;
      if (!active || !this.near || this.reduced.matches) {video.pause();return;}
      const device = this.mobile.matches ? 'mobile' : 'desktop';
      const url = video.dataset[device==='mobile'?'rpV2Mobile':'rpV2Desktop'] || video.dataset.rpV2Desktop;
      const poster = video.dataset[device==='mobile'?'rpV2MobilePoster':'rpV2DesktopPoster'] || video.dataset.rpV2DesktopPoster;
      if (video.getAttribute('src') !== url) {
        video.parentElement.classList.remove('rp-v2-loaded');
        video.poster = poster;
        video.src = url;
        video.preload = 'metadata';
        video.load();
      }
      if(this.visible && !document.hidden) {
        const play = video.play();
        if(play) play.then(() => {
          if(this.destroyed || !this.visible || document.hidden || this.reduced.matches || (this.scenes.length && i!==this.active)) video.pause();
        }).catch(() => {/* Keep the readable poster if autoplay is denied. */});
      } else video.pause();
    });
  }
  destroy() {
    this.destroyed=true;
    this.abort.abort();
    this.nearObserver.disconnect();
    this.visibleObserver.disconnect();
    cancelAnimationFrame(this.frame);
    this.release();
  }
}
function initialize(scope) {
  const candidates = [...scope.querySelectorAll('[data-rp-v2-root]')];
  if(scope.matches?.('[data-rp-v2-root]')) candidates.unshift(scope);
  candidates.forEach(root => {if(!roots.has(root)) roots.set(root,new RopetSection(root));});
}
function dispose(scope) {
  for(const [root,controller] of roots) {
    if(root===scope || scope.contains(root)) {controller.destroy();roots.delete(root);}
  }
}
initialize(document);
document.addEventListener('shopify:section:load', event => initialize(event.target));
document.addEventListener('shopify:section:unload', event => dispose(event.target));
document.addEventListener('shopify:section:select', event => {
  initialize(event.target);
  for(const [root,controller] of roots) if(event.target.contains(root)) controller.update();
});
